import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../components/PageBlock";
import {Accordion, AccordionBody, AccordionHeader, Button, CardBody} from "@material-tailwind/react";
import {useNavigate, useParams} from "react-router-dom";
import {User} from "../../types/User";
import {useUserContext} from "../../types/contexts/UserContext";
import FormSkeleton from "../../components/Skeletons/FormSkeleton";
import BrokerAvatar from "../../components/BrokerAvatar";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Area} from "react-easy-crop";
import onUpload from "../../components/CropImage/helpers/onUpload";
import {Size} from "react-easy-crop/types";
import {useToastContext} from "../../types/contexts/ToastContext";
import CropImg from "../../components/CropImage/CropImg";
import {Shape} from "../../components/CropImage/types/Shape";
import useComponentVisible from "../../hooks/useComponentVisible";
import Modal from "../../components/Modal/Modal";
import {getCroppedImage} from "../../components/CropImage/helpers/getCroppedImage";
import {ImageSize} from "../../components/CropImage/types/ImageSize";
import {API} from "../../types/API";
import {AxiosError, AxiosResponse} from "axios";
import {ErrorHandler} from "../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import StatusLabel from "../../components/StatusLabel";
import OutlineInput from "../../components/OutlineInput";
import OtpInput from "../../components/OtpInput";
import Loader from "../../assets/icons/Loader";
import {clear, validateData} from "../../reducers/validationReducer";
import {InputType} from "../../types/InputType";
import {useDispatch, useSelector} from "react-redux";
import OutlineTextarea from "../../components/OutlineTextarea";
import {ProfileAccordion} from "../../types/ProfileAccordion";
import {useSystemContext} from "../../types/contexts/SystemContext";
import DropdownInput from "../../components/DropdownInput";
import CountryAvatar from "../../components/CountryAvatar";
import {useTranslation} from "react-i18next";
import Card from "../../components/Card";
import {setTitle} from "../../hooks/setTitle";
import {imageToBytes} from "../../components/CropImage/helpers/imageToBytes";
import {APILink} from "../../types/APILink";
import {
    BrokerVirtualTerminalCountry,
    BrokerVirtualTerminalResponse
} from "../../types/Responses/VirtualTerminalResponse";
import {startRegistration} from '@simplewebauthn/browser';
import {setSidebarTitle} from "../../reducers/sidebar";
import {RootState} from "../../store";
import {getError, hasError} from "../../hooks/errors";

const Profile = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const userContext = useUserContext();
    const toast = useToastContext();
    const system = useSystemContext();
    const handleError = useErrorHandler();
    const navigate = useNavigate();

    const [user, setUser] = useState<User>();
    const [isReady, setIsReady] = useState<boolean>(false);
    const [accordions, setAccordions] = useState<ProfileAccordion>({
        form: false,
        wire: false,
        permissions: false,
        staff: false,
        links: false,
        baseCurrency: false,
        paymentMethods: false
    });

    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [timezone, setTimezone] = useState<number>(1);
    const [baseCurrency, setBaseCurrency] = useState<number>(1);
    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);

    const [isTFAProgress, setIsTFAProgress] = useState<boolean>(false);
    const [password, setPassword] = useState<string>();
    const [qr, setQr] = useState<string>();
    const [code, setCode] = useState<string>();
    const sanitizedCode = useMemo(() => (code ?? '').replace(/\s/g, ''), [code]);

    const [europeWire, setEuropeWire] = useState<string>("");
    const [nonEuropeWire, setNonEuropeWire] = useState<string>("");

    const [notifyURL, setNotifyURL] = useState<string>("");
    const [redirectURL, setRedirectURL] = useState<string>("");
    const [brokerPaymentMethods, setBrokerPaymentMethods] = useState<BrokerVirtualTerminalResponse>();
    const [isBrokerPaymentMethodsLoading, setBrokerPaymentMethodsLoading] = useState<boolean>(false);
    const [selectedPaymentCountry, setSelectedPaymentCountry] = useState<number>();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number>();

    const [showCropper, setShowCropper] = useState<boolean>(false);
    const [uploadedFile, setUploadedFile] = React.useState<string>('');
    const hiddenFileInput = React.useRef<HTMLInputElement>(null);
    const [cropped, setCropped] = React.useState<Area>();
    const [withCrop, setWithCrop] = useState<boolean>(true);
    const [cropImageArea, setCropImageArea] = React.useState<HTMLImageElement>();
    const dispatch = useDispatch();
    const modals = {
        avatar: useComponentVisible(false),
        two_fa: useComponentVisible(false)
    };
    const validationErrors = useSelector((state: RootState) => state.validation.errors);

    const sendRequest = useCallback(() => {
        if (!userContext || !system || !system.system || !toast)
            return;

        if (!id && !user) {
            setUser(userContext.user);
            setFirstName(userContext.user.first_name);
            setLastName(userContext.user.last_name);
            setBaseCurrency(system.system.currencies.find(c => c.code === userContext.user.currency)?.id ?? 5);
            setTimezone(system.system.timezones.find(t => t.name === userContext.user.timezone)?.id ?? 1);
            if (userContext.user.wire) {
                setEuropeWire(userContext.user.wire.europe);
                setNonEuropeWire(userContext.user.wire.non_europe);
            }
            if (userContext.user.links) {
                setRedirectURL(userContext.user.links.redirect_url);
                setNotifyURL(userContext.user.links.notify_url);
            }
            if (!userContext.user.two_fa) {
                toast?.show('Please setup 2FA', 'bg-orange-400');
            }
            setIsReady(true);
            setTitle(t('admin.profile.title'));
        } else {
            if (id && !user) {
                API.getUser(parseInt(id)).then((response: AxiosResponse) => {
                    setTitle(response.data.data.first_name + " " + response.data.data.last_name);
                    dispatch(setSidebarTitle(response.data.data.first_name + " " + response.data.data.last_name));
                    setUser(response.data.data as User);
                    setFirstName(response.data.data.first_name);
                    setLastName(response.data.data.last_name);
                    // @ts-expect-error can be undefined
                    setBaseCurrency(system.system.currencies.find(c => c.code === response.data.data.currency)?.id ?? 5);
                    // @ts-expect-error can be undefined
                    setTimezone(system.system.timezones.find(t => t.name === response.data.data.timezone)?.id ?? 1);
                    if (response.data.data.wire) {
                        setEuropeWire(response.data.data.wire.europe);
                        setNonEuropeWire(response.data.data.wire.non_europe);
                    }
                    if (response.data.data.links) {
                        setRedirectURL(response.data.data.links.redirect_url);
                        setNotifyURL(response.data.data.links.notify_url);
                    }
                    setIsReady(true);
                }).catch(e => ErrorHandler(e, `get${APILink.GET_USER.replace('$user', id.toString())}`, toast, handleError));
            }
        }
    }, [id, user, userContext, system, toast, setUser, setIsReady]);

    const loadBrokerPaymentMethods = useCallback((userId: number) => {
        setBrokerPaymentMethodsLoading(true);
        API.getBrokerEnabledMethods(userId).then((response: AxiosResponse) => {
            setBrokerPaymentMethods(response.data as BrokerVirtualTerminalResponse);
            setSelectedPaymentCountry(undefined);
            setSelectedPaymentMethod(undefined);
            setBrokerPaymentMethodsLoading(false);
        }).catch(e => {
            if (e instanceof AxiosError && e.response && e.response.status !== 403) {
                ErrorHandler(e, `get${APILink.GET_BROKER_ENABLED_METHODS.replace('$user', userId.toString())}`, toast, handleError);
            }
            setBrokerPaymentMethodsLoading(false);
        });
    }, [toast, handleError]);

    const userName = useCallback(() => {
        if (!user)
            return '';
        return <h4
            className="font-bold text-xl mb-1 text-black dark:text-white">{user.first_name} {user.last_name}</h4>;
    }, [user]);

    const handleClick = useCallback(() => {
        // @ts-expect-error can be undefined
        hiddenFileInput.current.click();
    }, [hiddenFileInput]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const {files} = event.target;
        if (files !== null) {
            try {
                onUpload(files[0], setUploadedFile);
                setShowCropper(true);
                modals.avatar.setIsComponentVisible(true);
            } catch (e) {
                // @ts-expect-error can be undefined
                toast?.show(e.message)
            }
        }
        // @ts-expect-error can be undefined
        hiddenFileInput.current.value = '';
    }, [modals.avatar, hiddenFileInput, toast, setShowCropper, setUploadedFile]);

    const onCroppedImage = useCallback((_crop: Area, percentCrop: Area, defaultSize: Size | undefined) => {
        if (defaultSize && (percentCrop.width === 0 || percentCrop.height === 0)) {
            percentCrop = {x: 0, y: 0, ...defaultSize};
        }
        setCropped(percentCrop);
    }, [setCropped]);

    const handlePasskeyButtonClicked = useCallback(() => {
        if (user) {
            setCode('');
            API.getPasskeyOptions().then(async (response) => {
                const attestationResponse = await startRegistration(response.data);
                API.createPasskey(attestationResponse).then((r) => {
                    if (r.data.status === true) {
                        setUser({...user, passkeys: true});
                        toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                                       className="mr-4"/>{t('admin.profile.passkeys.success_message')}</>, "bg-teal-500");
                        const currentUser = {
                            email: user.email,
                            name: `${user.first_name} ${user.last_name}`,
                        };
                        if (localStorage.getItem('passkeys') ?? null) {
                            const storedPasskeys: {
                                email: string;
                                name: string
                            }[] = JSON.parse(atob(localStorage.getItem('passkeys') ?? '') ?? '[]');
                            if (!storedPasskeys.map(s => s.email).includes(currentUser.email)) {
                                storedPasskeys.push(currentUser);
                            }
                            localStorage.setItem('passkeys', btoa(JSON.stringify(storedPasskeys)));
                        } else {
                            localStorage.setItem('passkeys', btoa(JSON.stringify([currentUser])));
                        }
                    } else {
                        toast?.show(<>Something went wrong</>, "bg-red-500")
                    }
                }).catch(e => ErrorHandler(e, `post${APILink.REGISTER_PASSKEY}`, toast, handleError));
            }).catch(e => ErrorHandler(e, `get${APILink.GET_PASSKEY_OPTIONS}`, toast, handleError));
        }
    }, [user, toast]);

    const handleSuccessResponse = useCallback((response: AxiosResponse, message: JSX.Element | string) => {
        const localUser = response.data.data as User
        if (localUser && user) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500")
            if (!id || parseInt(id) === userContext?.user.id) {
                userContext?.setUser(localUser);
            }
            setUser({...user, ...localUser});
            setTimeout(() => setValidated(false), 3000);
        }
    }, [toast, userContext, id, user]);

    const uploadAvatar = useCallback(() => {
        if (showCropper && (!cropImageArea || !cropped)) {
            toast?.show(t('admin.images.empty_error'), "bg-red-500");
            return;
        }

        const iTemp = cropImageArea && cropped && withCrop ? getCroppedImage(cropImageArea, cropped, ImageSize.avatar) : (!withCrop ? imageToBytes(document.getElementById('non-cropped-image') as HTMLImageElement) : '');
        const request = {image: iTemp}
        API.uploadAvatar(request).then((response: AxiosResponse) => {
            handleSuccessResponse(response, t('admin.profile.avatar_changed'));
            const user = response.data.data as User
            if (user) {
                modals.avatar.setIsComponentVisible(false)
            }
        }).catch(e => ErrorHandler(e, `post${APILink.UPLOAD_AVATAR}`, toast, handleError));

    }, [showCropper, cropImageArea, cropped, modals.avatar, t, withCrop]);

    const deleteUser = useCallback(() => {
        if (user) {
            API.deleteUser(user.id).then(() => {
                setUser({...user, status: "Inactive"});
            }).catch(e => ErrorHandler(e, `delete${APILink.GET_USER.replace('$user', user.id.toString())}`, toast, handleError));
        }
    }, [user, toast, handleError]);

    const onFirstNameChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);


    const onLastNameChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);

    const onPasswordChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setPassword(e.currentTarget.value)
        }
    }, [setValidated, setPassword]);

    const onCodeChanged = useCallback((value: string) => {
        setValidated(false);
        setCode(value.replace(/\s/g, ''));
    }, [setValidated, setCode]);

    const onEuropeWireChanged = useCallback((e: React.ChangeEvent<HTMLTextAreaElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEuropeWire(e.currentTarget.value)
        }
    }, [setValidated, setEuropeWire]);

    const onNonEuropeWireChanged = useCallback((e: React.ChangeEvent<HTMLTextAreaElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setNonEuropeWire(e.currentTarget.value)
        }
    }, [setValidated, setNonEuropeWire]);

    const onNotifyUrlChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setNotifyURL(e.currentTarget.value)
        }
    }, [setValidated, setNotifyURL]);

    const onRedirectUrlChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setRedirectURL(e.currentTarget.value)
        }
    }, [setValidated, setRedirectURL]);


    const callback = useCallback((b: boolean) => {
        setValidated(true);
        setInProgress(false);
        if (!user)
            return;

        if (b) {
            API.updateUserMe({
                first_name: firstName,
                last_name: lastName,
                timezone_id: timezone,
                base_currency_id: baseCurrency
            }).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.profile.updated'))).catch(e => ErrorHandler(e, `patch${APILink.ME}`, toast, handleError));
        }
    }, [firstName, lastName, baseCurrency, timezone, user, toast, handleError, handleSuccessResponse, id, setValidated, setInProgress]);

    const submitProfileForm = useCallback(() => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [
                    {
                        key: 'first_name',
                        label: t('admin.profile.first_name'),
                        type: InputType.text,
                        regex: '^.{3,}\\b$'
                    },
                    {key: 'last_name', label: t('admin.profile.last_name'), type: InputType.text, regex: '^.{3,}\\b$'},
                    {key: 'timezone', label: t('admin.profile.timezone'), type: InputType.text, regex: '^.{1,3}\\b$'},
                ],
                introducedData: {
                    first_name: firstName,
                    last_name: lastName,
                    timezone
                },
                callback: callback
            }
        }));
    }, [firstName, lastName, timezone, callback]);

    const submitBaseCurrencyForm = useCallback(() => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [
                    {
                        key: 'base_currency',
                        label: t('admin.profile.base_currency'),
                        type: InputType.text,
                        regex: '^.{1,3}\\b$'
                    },
                ],
                introducedData: {
                    base_currency: baseCurrency
                },
                callback: callback
            }
        }));
    }, [baseCurrency, callback]);

    const wireCallback = useCallback((b: boolean) => {
        setValidated(true);
        setInProgress(false);
        if (!user)
            return;

        if (b) {
            API.setWire({
                sepa: europeWire,
                swift: nonEuropeWire
            }).then((response: AxiosResponse) => handleSuccessResponse(response, <>Wire data updated
                successful</>)).catch(e => ErrorHandler(e, `patch${APILink.SET_WIRE}`, toast, handleError));
        }
    }, [europeWire, nonEuropeWire, user, toast, handleError, handleSuccessResponse, id, setValidated, setInProgress]);

    const submitWireForm = useCallback(() => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [
                    {
                        key: 'sepa',
                        label: t('admin.profile.sepa'),
                        type: InputType.text,
                        regex: '^(\\r\\n|\\r|\\n|.){25,}$'
                    },
                    {
                        key: 'swift',
                        label: t('admin.profile.swift'),
                        type: InputType.text,
                        regex: '^(\\r\\n|\\r|\\n|.){25,}$'
                    },
                ],
                introducedData: {
                    sepa: europeWire,
                    swift: nonEuropeWire
                },
                callback: wireCallback
            }
        }));
    }, [wireCallback, europeWire, nonEuropeWire]);

    const linksCallback = useCallback((b: boolean) => {
        setValidated(true);
        setInProgress(false);
        if (!user)
            return;

        if (b) {
            API.setBrokerLinks({
                notify_url: notifyURL,
                redirect_url: redirectURL
            }).then((response: AxiosResponse) => handleSuccessResponse(response, <>Notification links updated
                successful</>)).catch(e => ErrorHandler(e, `patch${APILink.SET_BROKER_LINKS}`, toast, handleError));
        }
    }, [notifyURL, redirectURL, user, toast, handleError, handleSuccessResponse, id, setValidated, setInProgress]);

    const submitLinksForm = useCallback(() => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [
                    {key: 'notify_url', label: t('admin.profile.notify_url'), type: InputType.text, regex: '^.{15,}$'},
                    {
                        key: 'redirect_url',
                        label: t('admin.profile.redirect_url'),
                        type: InputType.text,
                        regex: '^.{15,}$'
                    },
                ],
                introducedData: {
                    notify_url: notifyURL,
                    redirect_url: redirectURL
                },
                callback: linksCallback
            }
        }));
    }, [linksCallback, notifyURL, redirectURL]);

    const onConfirm2FACallback = useCallback((b: boolean) => {
        if (!user)
            return;

        if (b) {
            API.confirm2FA(sanitizedCode).then((response: AxiosResponse) => {
                setIsTFAProgress(false);
                setUser(response.data.data as User);
                setValidated(false);
                setCode('');
                setQr('');
                dispatch(clear());
                modals.two_fa.setIsComponentVisible(false);
                toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                               className="mr-4"/>{t('admin.profile.enabled_2fa')}</>, "bg-teal-500")
            }).catch((e) => {
                setIsTFAProgress(false);
                ErrorHandler(e, `post${APILink.CONFIRM_2FA}`, toast, handleError)
            });
        } else setIsTFAProgress(false);

    }, [user, sanitizedCode, modals, toast, handleError]);

    const onSwitch2FACallback = useCallback((b: boolean) => {
        if (!user)
            return;

        setValidated(true);
        if (b) {
            API.switch2FA(password ?? '').then((response: AxiosResponse) => {
                setIsTFAProgress(false);
                setUser(response.data.data as User);
                setValidated(false);
                dispatch(clear());
                if (typeof response.data.data.qr !== "undefined") {
                    setQr(response.data.data.qr);
                } else {
                    modals.two_fa.setIsComponentVisible(false);
                }

                toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                               className="mr-4"/>{user.two_fa ? t('admin.profile.disabled_2fa') : t('admin.profile.enabled_2fa')}</>, "bg-teal-500")
            }).catch((e) => {
                setIsTFAProgress(false);
                ErrorHandler(e, `post${APILink.SWITCH_2FA}`, toast, handleError)
            });
        } else setIsTFAProgress(false);

    }, [user, password, toast, handleError, modals]);

    const changeTimezone = useCallback((s: string | number) => {
        setTimezone(typeof s === 'string' ? parseInt(s) : s);
    }, [setTimezone]);

    const changeBaseCurrency = useCallback((s: string | number) => {
        setBaseCurrency(typeof s === 'string' ? parseInt(s) : s);
        setValidated(false)
    }, [setBaseCurrency]);

    const onTwoFAClicked = useCallback(() => {
        if (!user)
            return;
        setIsTFAProgress(true);
        if (!qr) {
            onSwitch2FACallback(true);
        } else {
            dispatch(validateData({
                payload: {
                    inputs: [{
                        key: 'code',
                        label: t('admin.profile.code'),
                        type: InputType.number,
                        regex: '^\\d{6}\\b$'
                    }],
                    introducedData: {code: sanitizedCode},
                    callback: onConfirm2FACallback
                }
            }));
        }
    }, [user, qr, sanitizedCode, onSwitch2FACallback, onConfirm2FACallback]);

    const avatarModalUI = useMemo(() => {
        if (!modals.avatar.isComponentVisible)
            return <></>;

        return <Modal refer={modals.avatar.ref} showModal={modals.avatar.isComponentVisible} withoutPadding={true}
                      title={t('admin.profile.upload_avatar')}>
            <>
                <FontAwesomeIcon icon={solid('xmark')} size="lg"
                                 className="cursor-pointer absolute top-8 right-4 dark:text-white"
                                 onClick={() => modals.avatar.setIsComponentVisible(false)}/>
                <div className="w-[50%] py-4">
                    {showCropper && <div className="flex relative">
                        <CropImg withoutLeft setWithCropCallback={setWithCrop} shape={Shape.ROUND} ratio={1}
                                 img={uploadedFile} maxWith="12rem"
                                 onCropCompleted={onCroppedImage} onCropLoaded={(i) => setCropImageArea(i)}/>
                    </div>}
                    <div className="mt-4 pt-4 text-center">
                        <Button className="bg-teal-500 mb-1" onClick={() => uploadAvatar()}>{t('Confirm')}</Button>
                    </div>
                    <div className="mt-1 text-center">
                    <span className="mt-1 cursor-pointer text-gray-400 dark:text-gray-700"
                          onClick={() => modals.avatar.setIsComponentVisible(false)}>{t('close')}</span>
                    </div>
                </div>
            </>
        </Modal>
    }, [modals.avatar, modals.avatar.isComponentVisible, showCropper, onCroppedImage, uploadedFile, uploadAvatar]);

    const twoFAUI = useMemo(() => {
        if (!modals.two_fa.isComponentVisible || !user)
            return <></>;

        return <Modal refer={modals.two_fa.ref} showModal={modals.two_fa.isComponentVisible}
                      title={user.two_fa ? t('admin.profile.disable_2fa') : t('admin.profile.enable_2fa')}>
            <>
                <FontAwesomeIcon icon={solid('xmark')} size="lg"
                                 className="cursor-pointer absolute top-8 right-4 dark:text-white"
                                 onClick={() => modals.two_fa.setIsComponentVisible(false)}/>
                <div className="w-full text-center py-4">
                    {!qr ?
                        <p className="mb-4">{user.two_fa ? t('admin.profile.disable_2fa_message') : t('admin.profile.enable_2fa_message')}</p> : ''}
                    {qr ? <img src={qr}
                               className="w-64 h-64 inline-block border-8 dark:border-blue-gray-500 rounded-xl mb-4"/> : ''}
                    {qr ? <div className="mb-4">{t('admin.profile.qr')}</div> : ''}
                    {!qr ? <OutlineInput className="initial" fieldName="password" name={t('admin.profile.password')}
                                         validated={validated} value={password}
                                         type="password" onChange={onPasswordChanged}/>
                        : <div className="initial max-w-md mx-auto">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                                {t('admin.profile.code')}
                            </label>
                            <OtpInput value={code ?? ''}
                                      onChange={onCodeChanged}
                                      error={validated && hasError('code', validationErrors)}
                                      className="my-3 justify-center"/>
                            {validated && hasError('code', validationErrors) &&
                                <label className="mt-1 text-red-500 text-xs block">
                                    {getError('code', validationErrors)}
                                </label>}
                        </div>}
                    <Button className="mt-8 bg-teal-500" onClick={onTwoFAClicked}>{isTFAProgress ?
                        <Loader/> : t('Confirm')}</Button>
                </div>
            </>
        </Modal>
    }, [modals.two_fa, modals.two_fa.isComponentVisible, isTFAProgress, onTwoFAClicked, onTwoFAClicked, user, validated, onCodeChanged, onPasswordChanged, qr, validationErrors]);

    const twoFAButtonUI = useMemo(() => {
        if (!isReady || !user || !userContext || (id && parseInt(id ?? '') !== userContext.user.id))
            return <></>

        return userContext.user.permissions.includes('enable/disable 2FA') ?
            <div className="mb-2">
                {twoFAUI}
                <Button className="w-full bg-black" onClick={() => {
                    setPassword('')
                    setCode('');
                    setQr('');
                    modals.two_fa.setIsComponentVisible(true);
                }}>{user.two_fa ? t('admin.profile.disable_2fa') : t('admin.profile.enable_2fa')}</Button>
            </div> : ''

    }, [userContext, isReady, user, id, twoFAUI, modals.two_fa]);

    const passkeysButtonUI = useMemo(() => {
        if (!isReady || !user || !userContext || (id && parseInt(id ?? '') !== userContext.user.id) || !user.two_fa)
            return <></>;

        return <div className="mb-2">
            <Button className="w-full bg-black"
                    onClick={handlePasskeyButtonClicked}>{user.passkeys ? t('admin.profile.passkeys.reset') : t('admin.profile.passkeys.setup')}</Button>
        </div>
    }, [userContext, isReady, user, handlePasskeyButtonClicked, id]);

    const leftSideUI = useMemo(() => {
        if (!user || !userContext)
            return <></>
        return <div className="text-center">
            <BrokerAvatar size="lg" image={user.image} imageClasses="h-20 w-20" className="mb-2 h-32 w-32"/>
            {userName()}
            <div className="text-center">
                {user.roles.map(role => <Button key={role}
                                                className="text-black dark:text-gray-500 bg-transparent shadow-none hover:shadow-none">{role}</Button>)}
            </div>
            {id && parseInt(id) !== userContext.user.id ?
                <div className="mb-6"><StatusLabel status={user.status}/></div> : ''}

            <div className="mt-8">
                {(!id && userContext.user.permissions.includes('upload avatar')) || (parseInt(id as string) === userContext.user.id && userContext.user.permissions.includes('upload avatar')) ?
                    <>
                        <Button className="bg-teal-500 w-full mb-2"
                                onClick={() => handleClick()}>{t('admin.profile.upload_avatar')}</Button>
                        {avatarModalUI}
                        <input type="file" accept="image/*" ref={hiddenFileInput} onChange={handleChange}
                               style={{display: 'none'}}/>
                    </> : ''}
                {twoFAButtonUI}
                {passkeysButtonUI}
                {userContext?.user.permissions.includes('update user') &&
                    <Button className="bg-black w-full mb-2" onClick={() => navigate('/app/admin/users/' + user.id)}>
                        <FontAwesomeIcon icon={solid("pen-to-square")} className="mr-2"/>
                        <span>{t('Edit')}</span>
                    </Button>}
                {userContext?.user.permissions.includes('delete user') && user.status !== "Inactive" &&
                    <Button className="bg-black w-full" onClick={() => deleteUser()}>
                        <FontAwesomeIcon icon={solid("trash")} className="mr-2 text-red-300"/>
                        <span>{t('Delete')}</span>
                    </Button>}
            </div>
        </div>;
    }, [user, twoFAButtonUI, passkeysButtonUI, userContext, avatarModalUI, userName, handleClick, hiddenFileInput, handleChange, uploadedFile, onCroppedImage, id]);

    const permissionsUI = useMemo(() => {
        if (!user || user.permissions.length === 0) {
            return <></>;
        }

        return <>
            <Accordion open={accordions.permissions}>
                <AccordionHeader onClick={() => setAccordions({...accordions, permissions: !accordions.permissions})}
                                 className="font-bold text-[1rem] mb-4 text-black dark:text-white">{t('admin.profile.permissions')}</AccordionHeader>
                <AccordionBody>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 md:gap-2 dark:text-gray-500">
                        {user.permissions.map(permission => <div
                            key={permission}>{permission.substring(0, 1).toUpperCase() + permission.substring(1)}</div>)}
                    </div>
                </AccordionBody>
            </Accordion>
        </>
    }, [user, accordions]);

    const paymentMethodCountriesOptions = useMemo(() => {
        if (!brokerPaymentMethods)
            return [];
        return brokerPaymentMethods.filters.countries.map(country => ({
            id: country.id,
            name: country.name,
            icon: <CountryAvatar country={country} withName={false} className="h-4"/>
        }));
    }, [brokerPaymentMethods]);

    const paymentMethodOptions = useMemo(() => {
        if (!brokerPaymentMethods)
            return [];
        return brokerPaymentMethods.filters.payment_methods.map(method => ({
            id: method.id,
            name: method.name,
            icon: method.image ? <img src={`${window.location.origin}/images/psps/${method.image}`} alt={method.name}
                                      className="h-4 w-8 object-contain mr-2 inline-block"/> : undefined
        }));
    }, [brokerPaymentMethods]);

    const filteredBrokerPaymentMethods = useMemo<BrokerVirtualTerminalCountry[]>(() => {
        if (!brokerPaymentMethods)
            return [];
        return brokerPaymentMethods.data
            .filter(country => !selectedPaymentCountry || country.country.id === selectedPaymentCountry)
            .map((country): BrokerVirtualTerminalCountry => ({
                ...country,
                payment_methods: country.payment_methods.filter(method => !selectedPaymentMethod || method.id === selectedPaymentMethod)
            }))
            .filter(country => country.payment_methods.length > 0);
    }, [brokerPaymentMethods, selectedPaymentCountry, selectedPaymentMethod]);

    const onPaymentCountryChanged = useCallback((value: string | number) => {
        const parsed = typeof value === "string" ? parseInt(value, 10) : value;
        if (typeof parsed !== "number" || Number.isNaN(parsed)) {
            setSelectedPaymentCountry(undefined);
            return;
        }
        setSelectedPaymentCountry(current => current === parsed ? undefined : parsed);
    }, []);

    const onPaymentMethodChanged = useCallback((value: string | number) => {
        const parsed = typeof value === "string" ? parseInt(value, 10) : value;
        if (typeof parsed !== "number" || Number.isNaN(parsed)) {
            setSelectedPaymentMethod(undefined);
            return;
        }
        setSelectedPaymentMethod(current => current === parsed ? undefined : parsed);
    }, []);

    const onClearPaymentFilters = useCallback(() => {
        setSelectedPaymentCountry(undefined);
        setSelectedPaymentMethod(undefined);
    }, [setSelectedPaymentCountry, setSelectedPaymentMethod]);

    const brokerPaymentMethodsUI = useMemo(() => {
        const isMe = user && userContext && user.id === userContext.user.id;
        const hasPermission = userContext && userContext.user.permissions.includes('see broker payment methods');
        if (!user || !user.roles.includes('Broker') || (!hasPermission && !isMe)) {
            return <></>;
        }

        const hasPaymentFilters = typeof selectedPaymentCountry !== "undefined" || typeof selectedPaymentMethod !== "undefined";

        return <>
            <Accordion open={accordions.paymentMethods}>
                <AccordionHeader onClick={() => setAccordions({
                    ...accordions,
                    paymentMethods: !accordions.paymentMethods
                })}
                                 className="font-bold text-[1rem] mb-4 text-black dark:text-white">{t('admin.profile.payment_methods')}</AccordionHeader>
                <AccordionBody>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-6 gap-2">
                        <DropdownInput
                            className="w-full"
                            name={t('admin.profile.payment_methods.country')}
                            list={paymentMethodCountriesOptions}
                            onChange={onPaymentCountryChanged}
                            selected={selectedPaymentCountry}
                            searchable={true}
                        />
                        <DropdownInput
                            className="w-full"
                            name={t('admin.profile.payment_methods.method')}
                            list={paymentMethodOptions}
                            onChange={onPaymentMethodChanged}
                            selected={selectedPaymentMethod}
                            searchable={true}
                        />
                        {hasPaymentFilters &&
                            <div className="w-full flex items-center justify-start mb-4 lg:mb-auto">
                                <Button
                                    className="w-auto text-black dark:text-white bg-blue-gray-500 dark:bg-blue-gray-700"
                                    onClick={onClearPaymentFilters}>
                                    {t('Clear')}
                                </Button>
                            </div>
                        }
                    </div>
                    {isBrokerPaymentMethodsLoading ? <div className="flex justify-center py-6"><Loader/>
                    </div> : filteredBrokerPaymentMethods.length === 0 ?
                        <div
                            className="text-sm text-blue-gray-500 dark:text-gray-400">{t('admin.profile.payment_methods.empty')}</div> :
                        filteredBrokerPaymentMethods.map(country => <div key={country.country.id} className="mb-8">
                            <div className="flex items-center text-lg font-semibold mb-4 dark:text-white">
                                <CountryAvatar country={country.country} className="h-4"/>
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {country.payment_methods.map(method => {
                                    const image = method.image ? `${window.location.origin}/images/psps/${method.image}` : null;
                                    return <div key={`${country.country.id}-${method.id}`}
                                                className="relative group border border-blue-gray-100 dark:border-blue-gray-800 rounded-xl p-4 bg-white dark:bg-blue-gray-900 shadow-sm flex flex-col items-center justify-center text-center min-h-[8rem]">
                                        {image ?
                                            <img src={image} alt={method.name}
                                                 className="h-10 w-16 object-contain"/> :
                                            <span
                                                className="text-2xl font-semibold uppercase">{method.name.substring(0, 1)}</span>}
                                        <span
                                            className="mt-2 text-sm font-semibold text-blue-gray-900 dark:text-white">{method.name}</span>
                                        <div
                                            className="absolute inset-0 flex flex-col items-center justify-center px-3 py-2 text-xs text-white bg-black bg-opacity-80 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            <div
                                                className="flex flex-col items-center justify-center w-full h-full gap-2 overflow-y-auto pointer-events-auto">
                                                {method.currencies.map(currency =>
                                                    <div className="grid grid-cols-3 w-full"
                                                         key={`${method.id}-${currency.currency}`}>
                                                        <div className="font-semibold">
                                                            {currency.code ?? currency.currency}
                                                        </div>
                                                        <div className="text-red-400 text-center">
                                                            {currency.min}
                                                        </div>
                                                        <div className="text-green-400 text-center">{currency.max}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                })}
                            </div>
                        </div>)}
                </AccordionBody>
            </Accordion>
        </>
    }, [user, userContext, selectedPaymentCountry, selectedPaymentMethod, accordions, t, paymentMethodCountriesOptions, onPaymentCountryChanged, paymentMethodOptions, onPaymentMethodChanged, onClearPaymentFilters, isBrokerPaymentMethodsLoading, filteredBrokerPaymentMethods]);

    const formUI = useMemo(() => {
        if (!isReady || !user || !userContext || (id && parseInt(id ?? '') !== userContext.user.id) || !system || !system.system) {
            return <></>
        }

        return <>
            <Accordion open={accordions.form}>
                <AccordionHeader onClick={() => setAccordions({...accordions, form: !accordions.form})}
                                 className="font-bold text-[1rem] mb-4 text-black dark:text-white">{t('admin.profile.personal_data')}</AccordionHeader>
                <AccordionBody>
                    <div className="mb-8">
                        <div className="lg:grid lg:grid-cols-6 lg:gap-2">
                            <OutlineInput fieldName='first_name' value={firstName} name={t('admin.profile.first_name')}
                                          type="text"
                                          validated={validated} onChange={onFirstNameChanged} className="col-span-2"/>
                            <OutlineInput fieldName='last_name' value={lastName} name={t('admin.profile.last_name')}
                                          type="text"
                                          validated={validated}
                                          onChange={onLastNameChanged} className="col-span-2"/>
                            <div className="lg:px-1 timezone">
                                <DropdownInput className="max-w-md lg:max-w-[90%]" menuClassName="lg:max-w-[150px]"
                                               onChange={changeTimezone} name={t('admin.profile.timezone')}
                                               list={system.system.timezones.map((t) => {
                                                   return {id: t.id, name: t.name.split('/')[1] ?? t.name}
                                               })}
                                               searchable={true}
                                               selected={timezone}/>
                            </div>
                            <Button className="bg-teal-500 h-10" disabled={isInProgress}
                                    onClick={submitProfileForm}>{isInProgress ?
                                <Loader/> : t('Submit')}</Button>
                        </div>
                    </div>
                </AccordionBody>
            </Accordion>
        </>

    }, [accordions, user, id, isReady, timezone, system, userContext, isInProgress, firstName, lastName, onFirstNameChanged, onLastNameChanged, validated]);

    const baseCurrencyUI = useMemo(() => {
        if (!isReady || !user || !userContext || (id && parseInt(id ?? '') !== userContext.user.id) || !system || !system.system) {
            return <></>
        }

        return <>
            <Accordion open={accordions.baseCurrency}>
                <AccordionHeader onClick={() => setAccordions({...accordions, baseCurrency: !accordions.baseCurrency})}
                                 className="font-bold text-[1rem] mb-4 text-black dark:text-white">{t('admin.profile.base_currency')}</AccordionHeader>
                <AccordionBody>
                    <div className="mb-8 max-w-md">
                        <DropdownInput className="max-w-md lg:max-w-[90%]" menuClassName="lg:max-w-[150px]"
                                       onChange={changeBaseCurrency} name={t('admin.profile.base_currency')}
                                       list={system.system.currencies.filter(c => !c.isCrypto).map((c) => {
                                           return {id: c.id, name: c.code}
                                       })}
                                       searchable={true}
                                       selected={baseCurrency}/>
                        <Button className="bg-teal-500 h-10" disabled={isInProgress}
                                onClick={submitBaseCurrencyForm}>{isInProgress ?
                            <Loader/> : t('Submit')}</Button>
                    </div>
                </AccordionBody>
            </Accordion>
        </>

    }, [accordions, user, id, isReady, baseCurrency, system, userContext, isInProgress, validated]);

    const brokerLinksUI = useMemo(() => {
        if (!isReady || !user || !userContext || (id && parseInt(id ?? '') !== userContext.user.id) || !user.roles.includes('Broker')) {
            return <></>
        }

        return <>
            <Accordion open={accordions.links}>
                <AccordionHeader onClick={() => setAccordions({...accordions, links: !accordions.links})}
                                 className="font-bold text-[1rem] mb-4 text-black dark:text-white">{t('admin.profile.notification_links')}</AccordionHeader>
                <AccordionBody>
                    <div className="mb-8">
                        <div className="grid grid-cols-6 gap-2">
                            <OutlineInput fieldName='notify_url' value={notifyURL} name={t('admin.profile.notify_url')}
                                          type="text"
                                          validated={validated} onChange={onNotifyUrlChanged} className="col-span-2"/>
                            <OutlineInput fieldName='redirect_url' value={redirectURL}
                                          name={t('admin.profile.redirect_url')} type="text"
                                          validated={validated}
                                          onChange={onRedirectUrlChanged} className="col-span-2"/>
                            <div></div>
                            <Button className="bg-teal-500" disabled={isInProgress}
                                    onClick={submitLinksForm}>{isInProgress ?
                                <Loader/> : t('Submit')}</Button>
                        </div>
                    </div>
                </AccordionBody>
            </Accordion>
        </>

    }, [accordions, user, id, isReady, system, userContext, isInProgress, validated, notifyURL, redirectURL, onRedirectUrlChanged, onNotifyUrlChanged]);

    const brokerWireUI = useMemo(() => {
        if (!isReady || !user || !userContext || (id && parseInt(id ?? '') !== userContext.user.id) || !user.roles.includes('Broker'))
            return <></>

        return <>
            <Accordion open={accordions.wire}>
                <AccordionHeader onClick={() => setAccordions({...accordions, wire: !accordions.wire})}
                                 className="font-bold text-[1rem] mb-4 text-black dark:text-white">{t('admin.profile.wire')}</AccordionHeader>
                <AccordionBody>
                    <div className="mb-8">
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <OutlineTextarea validated={validated} value={europeWire} onChange={onEuropeWireChanged}
                                                 name={t('admin.profile.sepa')} fieldName="sepa"/>
                            </div>
                            <div>
                                <OutlineTextarea validated={validated} value={nonEuropeWire}
                                                 onChange={onNonEuropeWireChanged} name={t('admin.profile.swift')}
                                                 fieldName="swift"/>
                            </div>
                        </div>
                        <div className="mt-4">
                            <p className="dark:text-gray-500 mb-4">{t('admin.profile.wire_message')}</p>
                            <Button className="bg-teal-500" disabled={isInProgress}
                                    onClick={submitWireForm}>{isInProgress ?
                                <Loader/> : t('Submit')}</Button>
                        </div>
                    </div>
                </AccordionBody>
            </Accordion>
        </>

    }, [accordions, user, id, isReady, userContext, isInProgress, validated, europeWire, nonEuropeWire, onEuropeWireChanged, onNonEuropeWireChanged]);

    const reportedUI = useMemo(() => {
        if (!isReady || !user || !userContext || !user.reported || (!user.reported.user && !user.reported.staff.length))
            return <></>

        return <>
            <Accordion open={accordions.staff}>
                <AccordionHeader onClick={() => setAccordions({...accordions, staff: !accordions.staff})}
                                 className="font-bold text-[1rem] mb-4 text-black dark:text-white">{t('admin.profile.staff')}</AccordionHeader>
                <AccordionBody>
                    {user.reported && user.reported.user ? <div className="dark:text-gray-500">
                        <h3 className="w-full text-md font-bold mb-4">{t('admin.profile.reported_to')}</h3>
                        <div className="inline-block">
                            <a href={'/app/admin/users/' + user?.reported?.user?.id + '/profile'}
                               className="flex items-center">
                                <BrokerAvatar size="md" image={user.reported.user.image}/>
                                {user.reported.user.name}
                            </a>
                        </div>
                    </div> : ''}
                    {user.reported && user.reported.staff.length > 0 ? <div className="dark:text-gray-500">
                        <h3 className="w-full text-md font-bold mb-4">{t('admin.profile.staff')}</h3>
                        {user.reported.staff.map(u => <div key={u.name} className="inline-block mr-8">
                            <a href={'/app/admin/users/' + u.id + '/profile'} className="flex items-center">
                                <BrokerAvatar size="md" image={u.image}/>
                                {u.name}
                            </a>
                        </div>)}
                    </div> : ''}
                </AccordionBody>
            </Accordion>
        </>
    }, [user, isReady, accordions]);

    const emailUI = useMemo(() => {
        if (!user || !userContext || !userContext.user.roles.includes('Admin'))
            return <></>;

        return <>
            <div className="w-full mt-4 mb-4">
                <span className="text-md font-bold dark:text-white">{t('admin.profile.email')}: </span> <a
                href={`mailto:${user.email}`} className="text-teal-500">{user.email}</a>
            </div>
            <div className="w-full mt-4 mb-4">
                <button className="text-teal-500"
                        onClick={() => navigate(`/app/admin/logs/${user.id}`)}>{t('admin.profile.logs')}</button>
            </div>
        </>
    }, [user, userContext, navigate]);

    const lastLogin = useMemo(() => {
        if (!user || !userContext)
            return <></>;

        return <div className="w-full mt-4 mb-4">
            <span
                className="text-md font-bold dark:text-white">{t('admin.profile.last_login')} </span> {user.last_activity_at}
        </div>
    }, [user, userContext]);

    useEffect(() => {
        if (user && user.roles.includes('Broker')) {
            loadBrokerPaymentMethods(user.id);
        } else {
            setBrokerPaymentMethods(undefined);
            setSelectedPaymentCountry(undefined);
            setSelectedPaymentMethod(undefined);
            setBrokerPaymentMethodsLoading(false);
        }
    }, [user, loadBrokerPaymentMethods]);

    useEffect(() => {
        setTitle("Profile");
        dispatch(setSidebarTitle(t('admin.profile.title')));
        sendRequest();
        dispatch(clear());
    }, [sendRequest, dispatch]);


    return <PageBlock>
        {!isReady ? <FormSkeleton/> : <>
            <Card className="mt-6 lg:mt-12 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    <div className="grid grid-cols-1 gap-2 lg:grid-cols-5 md:grid-cols-3">
                        {leftSideUI}
                        <div className="md:col-span-2 lg:col-span-4">
                            {lastLogin}
                            {emailUI}
                            {baseCurrencyUI}
                            {formUI}
                            {brokerLinksUI}
                            {brokerWireUI}
                            {permissionsUI}
                            {brokerPaymentMethodsUI}
                            {reportedUI}
                        </div>
                    </div>
                </CardBody>
            </Card>
        </>}
    </PageBlock>
}
export default memo(Profile);
